import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { WifiOff, RefreshCw, CheckCircle, Clock, AlertTriangle, X } from 'lucide-react';

/**
 * OfflineBanner — shows one of four mutually-exclusive states in a fixed
 * bottom toast:
 *
 *   1. failed  (red)    — some submissions couldn't be sent; CTA: tap to retry
 *   2. offline (gray)   — browser is offline; explains what happens next
 *   3. success (green)  — something just synced; auto-dismisses after 5s
 *   4. syncing (amber)  — items pending retry; no CTA (SW handles it)
 *
 * The order above is also the precedence — a failed item should always
 * outrank a "syncing" pill, and a fresh success beats both.
 */
export const OfflineBanner = () => {
  const { t } = useTranslation();
  const {
    isOnline, queueCount, failedCount, failedItems,
    lastSyncResult, retryFailed, dismissFailed,
  } = useOfflineSync();

  // --- 1. FAILED (red) ---
  if (failedCount > 0) {
    const firstPath = failedItems?.[0]?.path || '';
    const firstLabel = labelForPath(firstPath, t);
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto" data-testid="sync-failed-banner">
        <div className="bg-red-600 text-white rounded-xl shadow-lg px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">
              {t('offlineSync.failedTitle', { count: failedCount, defaultValue_one: '1 submission failed', defaultValue_other: '{{count}} submissions failed' })}
            </p>
            <p className="text-xs text-red-100 mt-0.5 truncate">
              {firstLabel}
              {failedItems?.[0]?.lastError ? ` — ${failedItems[0].lastError}` : ''}
            </p>
            <div className="mt-2 flex items-center gap-3">
              <button
                onClick={retryFailed}
                className="bg-white text-red-700 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-red-50"
                data-testid="sync-retry-button"
              >
                <RefreshCw className="inline h-3 w-3 mr-1" />
                {t('offlineSync.tapToRetry', 'Tap to retry')}
              </button>
              <button
                onClick={dismissFailed}
                className="text-xs text-red-100 underline hover:text-white"
                data-testid="sync-dismiss-button"
              >
                {t('offlineSync.dismiss', 'Dismiss')}
              </button>
            </div>
          </div>
          <button
            onClick={dismissFailed}
            className="text-red-100 hover:text-white p-1 -mt-1 -mr-1"
            aria-label="Close"
            data-testid="sync-close-button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // --- 2. OFFLINE (gray) ---
  if (!isOnline) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto" data-testid="offline-banner">
        <div className="bg-gray-800 text-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
          <WifiOff className="h-5 w-5 flex-shrink-0 text-amber-400" />
          <div className="flex-1">
            <p className="text-sm font-medium">{t('offlineSync.offlineTitle', 'You are offline')}</p>
            {queueCount > 0 ? (
              <p className="text-xs text-gray-300 mt-0.5 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t('offlineSync.queued', { count: queueCount })}
              </p>
            ) : (
              <p className="text-xs text-gray-300 mt-0.5">
                {t('offlineSync.offlineDesc', 'Changes will be saved and sent when you reconnect.')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- 3. SUCCESS (green, auto-dismiss) ---
  if (lastSyncResult && lastSyncResult.results?.length > 0) {
    const succeeded = lastSyncResult.results.filter((r) => r.success).length;
    if (succeeded > 0) {
      return (
        <div className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto" data-testid="sync-success-banner">
          <div className="bg-green-600 text-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3 animate-slide-up">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                {t('offlineSync.syncComplete', { count: succeeded })}
              </p>
              {lastSyncResult.remaining > 0 && (
                <p className="text-xs text-green-200 mt-0.5">
                  {t('offlineSync.stillPending', { count: lastSyncResult.remaining })}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  // --- 4. SYNCING (amber) — only when there are pending retryable items ---
  if (queueCount > 0) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto" data-testid="syncing-banner">
        <div className="bg-amber-500 text-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
          <RefreshCw className="h-5 w-5 flex-shrink-0 animate-spin" />
          <div className="flex-1">
            <p className="text-sm font-medium">{t('offlineSync.syncing', 'Syncing...')}</p>
            <p className="text-xs text-amber-100 mt-0.5">
              {t('offlineSync.queued', { count: queueCount })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Map an API path → a short human label so the failed banner reads well
function labelForPath(path, t) {
  if (!path) return t('offlineSync.genericItem', 'Item');
  if (path.startsWith('/api/business/event')) return t('offlineSync.item.event', 'Event');
  if (path.startsWith('/api/business/offer')) return t('offlineSync.item.offer', 'Offer');
  if (path.startsWith('/api/business/me')) return t('offlineSync.item.businessProfile', 'Business profile update');
  if (path.startsWith('/api/reviews')) return t('offlineSync.item.review', 'Review');
  if (path.startsWith('/api/reports')) return t('offlineSync.item.report', 'Report');
  if (path.startsWith('/api/charity/apply')) return t('offlineSync.item.charity', 'Charity application');
  if (path.startsWith('/api/contact')) return t('offlineSync.item.contact', 'Contact message');
  return t('offlineSync.genericItem', 'Item');
}
